import { Link } from "react-router-dom";
import { Topbar } from "@/components/parcelio/topbar";
import { StatusPill } from "@/components/parcelio/status-pill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/parcelio/searchable-select";
import { Textarea } from "@/components/ui/textarea";
import { parcelsApi, directoryApi, settingsApi, postcodeApi, type Parcel, type ParcelImage, type DriverDirectoryEntry, type ClientDirectoryEntry, type CorporateAccount } from "@/lib/api";
import { formatDateTime, formatParcelValue, parcelStatusLabel, toLegacyStatus } from "@/lib/parcel-adapters";
import { Plus, Search, Download, Filter, Pencil, Camera, Upload, X, Eye, MapPinned } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { CameraCaptureDialog } from "@/components/parcelio/camera-capture-dialog";
import { SignaturePad } from "@/components/parcelio/signature-pad";
import { PostcodeAutocomplete } from "@/components/parcelio/postcode-autocomplete";
import { ZoneMappingDialog } from "@/components/parcelio/zone-mapping-dialog";
import { useAuth } from "@/stores/auth";

const tabs: { v: "all" | Parcel["status"]; l: string }[] = [
  { v: "all", l: "All" },
  { v: "intake", l: "Pending pickup" },
  { v: "assigned", l: "Assigned" },
  { v: "in_transit", l: "In transit" },
  { v: "delivered", l: "Delivered" },
  { v: "failed", l: "Failed" },
];

const emptyForm = {
  sender_contact: "",
  sender_name: "",
  sender_phone: "",
  sender_email: "",
  new_sender_name: "",
  new_sender_phone: "",
  new_sender_email: "",
  receiver_contact: "",
  receiver_name: "",
  receiver_phone: "",
  receiver_email: "",
  new_receiver_name: "",
  new_receiver_phone: "",
  new_receiver_email: "",
  pickup_addr: "",
  dropoff_addr: "",
  amount_naira: "",
  driver_id: "",
  item_description: "",
  client_id: "",
  new_client_name: "",
  new_client_phone: "",
  corporate_account_id: "",
};

const emptyEditForm = {
  receiver_name: "",
  receiver_phone: "",
  receiver_email: "",
  pickup_addr: "",
  dropoff_addr: "",
  amount_naira: "",
  driver_id: "",
  corporate_account_id: "",
};

/** File picker with a camera-capture option (opens the device camera on mobile) and a live preview. */
function ImagePicker({
  previewUrl,
  onPick,
  onClear,
  busy,
}: {
  previewUrl: string | null;
  onPick: (file: File | undefined) => void;
  onClear: () => void;
  busy?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  if (previewUrl) {
    return (
      <div className="relative inline-block">
        <img src={previewUrl} alt="Preview" className="h-28 w-28 rounded-lg border object-cover" />
        <button
          type="button"
          onClick={onClear}
          disabled={busy}
          className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground"
          aria-label="Remove photo"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])}
        />
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => fileRef.current?.click()}>
          <Upload className="mr-1.5 h-4 w-4" /> Upload photo
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setCameraOpen(true)}>
          <Camera className="mr-1.5 h-4 w-4" /> Take photo
        </Button>
      </div>
      <CameraCaptureDialog open={cameraOpen} onOpenChange={setCameraOpen} onCapture={onPick} facingMode="environment" />
    </>
  );
}

function csvEscape(v: string) {
  return `"${v.replace(/"/g, '""')}"`;
}

export default function Deliveries() {
  const token = useAuth((s) => s.session?.accessToken);
  const isCorporate = useAuth((s) => s.session?.kind === "corporate");
  const isPersonal = useAuth((s) => s.session?.kind === "personal");
  const isClient = useAuth((s) => s.session?.kind === "client");
  const roles = useAuth((s) => s.session?.roles) ?? [];
  const isDriverOnly = useAuth((s) => s.session?.kind === "staff") && roles.length > 0 && roles.every((r) => r === "driver");
  const hideCompanySelector = isCorporate || isPersonal || isClient || isDriverOnly;
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<"all" | Parcel["status"]>("all");
  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [drivers, setDrivers] = useState<DriverDirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const [clients, setClients] = useState<ClientDirectoryEntry[]>([]);
  const [corporates, setCorporates] = useState<CorporateAccount[]>([]);

  const [newOpen, setNewOpen] = useState(false);
  const [intakeImage, setIntakeImage] = useState<File | null>(null);
  const [intakeImagePreview, setIntakeImagePreview] = useState<string | null>(null);
  const [editUploadingImage, setEditUploadingImage] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [signatureSaved, setSignatureSaved] = useState(false);
  const [otpChannel, setOtpChannel] = useState<"sms" | "email" | null>(null);
  const [otpRequesting, setOtpRequesting] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [markingDelivered, setMarkingDelivered] = useState(false);
  const [postcodeEnabled, setPostcodeEnabled] = useState(false);

  useEffect(() => {
    settingsApi
      .get(token)
      .then((s) => setPostcodeEnabled(s.postcode_enrichment_enabled === true))
      .catch(() => {});
  }, [token]);
  const [newDeliveryZoneSuggestion, setNewDeliveryZoneSuggestion] = useState<{
    zone: string;
    fee_multiplier: number;
    suggested_driver: { id: string; name: string } | null;
  } | null>(null);

  // Resolve a zone suggestion from a plain free-text address (no postcode
  // typed) — reuses bulk-validate's geocode+reverse for a single address,
  // since typing a normal address (the common path) never otherwise tells
  // us a postcode client-side.
  const resolveZoneFromAddress = async (address: string) => {
    if (!postcodeEnabled || !address.trim()) return;
    try {
      const res = await postcodeApi.bulkValidate([address.trim()], token);
      const row = res.results[0];
      if (row?.status !== "ok" || !row.postcode) return;
      const zone = row.postcode.split("-")[0]?.toUpperCase();
      if (!zone) return;
      const suggestion = await postcodeApi.zoneSuggestion(zone, token);
      setNewDeliveryZoneSuggestion(suggestion);
    } catch {
      // best-effort — badge just doesn't appear
    }
  };

  const [zoneOpen, setZoneOpen] = useState(false);
  const [zoneGroups, setZoneGroups] = useState<
    Array<{ zone: string; parcels: Array<{ id: string; tracking_id: string; pickup_addr: string | null; dropoff_addr: string | null }> }>
  >([]);
  const [zoneLoading, setZoneLoading] = useState(false);
  const [zoneDriverByZone, setZoneDriverByZone] = useState<Record<string, string>>({});
  const [zoneAssigning, setZoneAssigning] = useState<string | null>(null);

  const openZoneGrouping = async () => {
    setZoneOpen(true);
    setZoneLoading(true);
    try {
      const res = await parcelsApi.unassignedByZone(token);
      setZoneGroups(res.zones);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load zone grouping");
    } finally {
      setZoneLoading(false);
    }
  };

  const assignZone = async (zone: string, parcelIds: string[]) => {
    const driverId = zoneDriverByZone[zone];
    if (!driverId) {
      toast.error("Pick a driver for this zone first");
      return;
    }
    setZoneAssigning(zone);
    try {
      const res = await parcelsApi.bulkAssign(parcelIds, driverId, token);
      toast.success(`Assigned ${res.updated} deliveries in zone ${zone}`);
      setZoneGroups((prev) => prev.filter((g) => g.zone !== zone));
      const updated = await parcelsApi.list(undefined, token);
      setParcels(updated);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk assignment failed");
    } finally {
      setZoneAssigning(null);
    }
  };

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<
    Array<{ address: string; status: "ok" | "no_postcode" | "error" | "skipped"; postcode?: string; error?: string }>
  >([]);
  const [bulkRunning, setBulkRunning] = useState(false);
  const bulkFileRef = useRef<HTMLInputElement>(null);

  const runBulkValidate = async (file: File) => {
    const text = await file.text();
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    // Accept either a header-less one-address-per-line file, or a CSV with
    // an "address" column — take the first column, skip an obvious header.
    const addresses = lines
      .map((l) => l.split(",")[0]?.replace(/^"|"$/g, "").trim() ?? "")
      .filter((a) => a && a.toLowerCase() !== "address");
    if (!addresses.length) {
      toast.error("No addresses found in file");
      return;
    }
    setBulkRunning(true);
    setBulkRows([]);
    try {
      const res = await postcodeApi.bulkValidate(addresses, token);
      setBulkRows(res.results);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk validation failed");
    } finally {
      setBulkRunning(false);
    }
  };

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);

  const [viewParcel, setViewParcel] = useState<Parcel | null>(null);
  const [viewImages, setViewImages] = useState<ParcelImage[]>([]);
  const [viewImagesLoading, setViewImagesLoading] = useState(false);

  const [editParcel, setEditParcel] = useState<Parcel | null>(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [editInitial, setEditInitial] = useState(emptyEditForm);
  const [confirmEditCloseOpen, setConfirmEditCloseOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // list_clients scopes to the caller's own contacts (corporate) or
        // returns [] (personal/client) rather than 403ing — safe to call
        // unconditionally now. The full corporate directory is still
        // staff-only server-side, so that one stays skipped.
        // list_drivers/list_clients (the full directories) are staff-only
        // server-side and denied outright for driver-only logins — a driver
        // has no reassignment UI anyway, so skip rather than 403 the load.
        const [p, d, c, corps] = await Promise.all([
          parcelsApi.list(undefined, token),
          isDriverOnly ? Promise.resolve([]) : directoryApi.drivers(token),
          isDriverOnly ? Promise.resolve([]) : directoryApi.clients(token),
          hideCompanySelector ? Promise.resolve([]) : directoryApi.corporates(token),
        ]);
        if (cancelled) return;
        setParcels(p);
        setDrivers(d);
        setClients(c);
        setCorporates(corps);
      } catch {
        toast.error("Failed to load deliveries");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = parcels.filter((p) => {
    if (tab !== "all" && p.status !== tab) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      p.tracking_id.toLowerCase().includes(s) ||
      (p.receiver_name ?? "").toLowerCase().includes(s)
    );
  });

  const dropdownOnlyKeys = new Set(["driver_id", "client_id", "sender_contact", "receiver_contact", "corporate_account_id"]);
  const isDirty = Object.entries(form).some(([k, v]) => !dropdownOnlyKeys.has(k) && v.trim() !== "");

  const requestClose = () => {
    if (isDirty) {
      setConfirmCloseOpen(true);
    } else {
      setNewOpen(false);
    }
  };

  const discardAndClose = () => {
    setForm(emptyForm);
    clearIntakeImage();
    setConfirmCloseOpen(false);
    setNewOpen(false);
  };

  const clearIntakeImage = () => {
    setIntakeImage(null);
    setIntakeImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const pickIntakeImage = (file: File | undefined) => {
    if (!file) return;
    clearIntakeImage();
    setIntakeImage(file);
    setIntakeImagePreview(URL.createObjectURL(file));
  };

  const createDelivery = async () => {
    const senderName = form.sender_contact === "__new__" ? form.new_sender_name.trim() : form.sender_name.trim();
    const senderPhone = form.sender_contact === "__new__" ? form.new_sender_phone.trim() : form.sender_phone.trim();
    const senderEmail = form.sender_contact === "__new__" ? form.new_sender_email.trim() : form.sender_email.trim();
    const receiverName = form.receiver_contact === "__new__" ? form.new_receiver_name.trim() : form.receiver_name.trim();
    const receiverPhone = form.receiver_contact === "__new__" ? form.new_receiver_phone.trim() : form.receiver_phone.trim();
    const receiverEmail = form.receiver_contact === "__new__" ? form.new_receiver_email.trim() : form.receiver_email.trim();

    if (!senderName || !receiverName || !form.pickup_addr.trim() || !form.dropoff_addr.trim()) {
      toast.error("Sender, receiver, pickup and dropoff address are required");
      return;
    }
    const amountNaira = Number(form.amount_naira);
    if (!amountNaira || amountNaira <= 0) {
      toast.error("Enter a valid declared value");
      return;
    }
    if (form.client_id === "__new__" && !form.new_client_name.trim()) {
      toast.error("New client name is required");
      return;
    }
    if (!hideCompanySelector && !form.corporate_account_id) {
      toast.error("Select the company this delivery belongs to");
      return;
    }
    setCreating(true);
    try {
      let clientId = form.client_id || undefined;
      if (form.client_id === "__new__") {
        const client = await directoryApi.createClient({
          full_name: form.new_client_name.trim(),
          phone: form.new_client_phone.trim() || undefined,
        }, token);
        setClients((cur) => [...cur, client]);
        clientId = client.id;
      }
      if (form.sender_contact === "__new__") {
        const contact = await directoryApi.createClient(
          { full_name: senderName, phone: senderPhone || undefined, email: senderEmail || undefined },
          token,
        );
        setClients((cur) => [...cur, contact]);
      }
      if (form.receiver_contact === "__new__") {
        const contact = await directoryApi.createClient(
          { full_name: receiverName, phone: receiverPhone || undefined, email: receiverEmail || undefined },
          token,
        );
        setClients((cur) => [...cur, contact]);
      }
      const created = await parcelsApi.create({
        sender_name: senderName,
        sender_phone: senderPhone || undefined,
        sender_email: senderEmail || undefined,
        receiver_name: receiverName,
        receiver_phone: receiverPhone || undefined,
        receiver_email: receiverEmail || undefined,
        pickup_addr: form.pickup_addr.trim(),
        dropoff_addr: form.dropoff_addr.trim(),
        amount_kobo: Math.round(amountNaira * 100),
        driver_id: form.driver_id || undefined,
        client_id: clientId,
        corporate_account_id: form.corporate_account_id || undefined,
        item_description: form.item_description.trim() || undefined,
      }, token);
      setParcels((cur) => [created, ...cur]);
      toast.success(`Delivery ${created.tracking_id} created`);
      if (intakeImage) {
        try {
          await parcelsApi.uploadImage(created.id, intakeImage, "intake", token);
        } catch {
          toast.error("Delivery created, but photo upload failed");
        }
      }
      setForm(emptyForm);
      clearIntakeImage();
      setNewOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create delivery");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (p: Parcel) => {
    const initial = {
      receiver_name: p.receiver_name ?? "",
      receiver_phone: "",
      receiver_email: "",
      pickup_addr: p.pickup_addr ?? "",
      dropoff_addr: p.dropoff_addr ?? "",
      amount_naira: (p.amount_kobo / 100).toFixed(2),
      driver_id: p.driver_id ?? "",
      corporate_account_id: p.corporate_account_id ?? "",
    };
    setEditParcel(p);
    setEditForm(initial);
    setEditInitial(initial);
    setSignatureSaved(false);
    setOtpChannel(null);
    setOtpCode("");
    setOtpVerified(false);
  };

  useEffect(() => {
    if (!viewParcel) {
      setViewImages([]);
      return;
    }
    let cancelled = false;
    setViewImagesLoading(true);
    parcelsApi
      .images(viewParcel.id, token)
      .then((imgs) => {
        if (!cancelled) setViewImages(imgs);
      })
      .catch(() => {
        if (!cancelled) setViewImages([]);
      })
      .finally(() => {
        if (!cancelled) setViewImagesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewParcel, token]);

  const isEditDirty = editParcel != null && JSON.stringify(editForm) !== JSON.stringify(editInitial);

  const requestEditClose = () => {
    if (isEditDirty) {
      setConfirmEditCloseOpen(true);
    } else {
      setEditParcel(null);
    }
  };

  const discardEditAndClose = () => {
    setConfirmEditCloseOpen(false);
    setEditParcel(null);
  };

  const requestOtp = async () => {
    if (!editParcel) return;
    setOtpRequesting(true);
    try {
      const res = await parcelsApi.requestDeliveryOtp(editParcel.id, token);
      setOtpChannel(res.channel);
      toast.success(`Code sent via ${res.channel}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send delivery code");
    } finally {
      setOtpRequesting(false);
    }
  };

  const verifyOtp = async () => {
    if (!editParcel || otpCode.length !== 6) return;
    setOtpVerifying(true);
    try {
      const res = await parcelsApi.verifyDeliveryOtp(editParcel.id, otpCode, token);
      if (res.verified) {
        setOtpVerified(true);
        toast.success("Code verified");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Incorrect or expired code");
    } finally {
      setOtpVerifying(false);
    }
  };

  const markDelivered = async () => {
    if (!editParcel) return;
    setMarkingDelivered(true);
    try {
      const res = await parcelsApi.scan(editParcel.id, "delivered", undefined, token);
      setParcels((cur) => cur.map((p) => (p.id === editParcel.id ? { ...p, status: res.status as Parcel["status"] } : p)));
      toast.success("Delivery marked as delivered");
      setEditParcel(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not mark as delivered — proof of delivery required");
    } finally {
      setMarkingDelivered(false);
    }
  };

  const saveEdit = async () => {
    if (!editParcel) return;
    if (!editForm.receiver_name.trim() || !editForm.pickup_addr.trim() || !editForm.dropoff_addr.trim()) {
      toast.error("Receiver, pickup, and dropoff address are required");
      return;
    }
    const amountNaira = Number(editForm.amount_naira);
    if (!amountNaira || amountNaira <= 0) {
      toast.error("Enter a valid declared value");
      return;
    }
    setSaving(true);
    try {
      const updated = await parcelsApi.update(editParcel.id, {
        receiver_name: editForm.receiver_name.trim(),
        receiver_phone: editForm.receiver_phone.trim() || undefined,
        receiver_email: editForm.receiver_email.trim() || undefined,
        pickup_addr: editForm.pickup_addr.trim(),
        dropoff_addr: editForm.dropoff_addr.trim(),
        amount_kobo: Math.round(amountNaira * 100),
        driver_id: editForm.driver_id || undefined,
        corporate_account_id: editForm.corporate_account_id || undefined,
      }, token);
      setParcels((cur) => cur.map((p) => (p.id === updated.id ? updated : p)));
      toast.success(`Delivery ${updated.tracking_id} updated`);
      setEditParcel(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update delivery");
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const header = ["Tracking", "Receiver", "Dropoff", "Driver", "Status", "Value (NGN)", "Updated"];
    const rows = filtered.map((p) => {
      const d = drivers.find((x) => x.id === p.driver_id);
      return [
        p.tracking_id,
        p.receiver_name ?? "",
        p.dropoff_addr ?? "",
        d?.name ?? "",
        parcelStatusLabel(p.status),
        (p.amount_kobo / 100).toFixed(2),
        p.updated_at ?? "",
      ];
    });
    const csv = [header, ...rows].map((r) => r.map((c) => csvEscape(String(c))).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `deliveries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Topbar
        title="Deliveries"
        subtitle={`${parcels.length} total`}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={exportCsv}>
              <Download className="mr-1.5 h-4 w-4" /> Export
            </Button>
            {isDriverOnly || !postcodeEnabled ? null : (
              <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}>
                <Upload className="mr-1.5 h-4 w-4" /> Bulk validate
              </Button>
            )}
            {isDriverOnly || !postcodeEnabled ? null : (
              <Button variant="outline" size="sm" onClick={openZoneGrouping}>
                <MapPinned className="mr-1.5 h-4 w-4" /> Zone grouping
              </Button>
            )}
            {isDriverOnly ? null : (
              <Button size="sm" onClick={() => setNewOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" /> New delivery
              </Button>
            )}
          </>
        }
      />
      <div className="flex-1 p-4 md:p-6">
        <div className="surface-card">
          <div className="space-y-3 border-b p-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-56">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search tracking # or recipient…"
                  className="pl-9"
                  aria-label="Search deliveries"
                />
              </div>
              <Button variant="outline" size="sm"><Filter className="mr-1.5 h-4 w-4" /> Filters</Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tabs.map((t) => (
                <button
                  key={t.v}
                  onClick={() => setTab(t.v)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    tab === t.v ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted"
                  )}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Tracking</th>
                  <th className="px-4 py-3 font-medium">Recipient</th>
                  <th className="px-4 py-3 font-medium">Driver</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Value</th>
                  <th className="px-4 py-3 font-medium">Updated</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={7}><LoadingBlock label="Loading deliveries…" /></td></tr> : null}
                {!loading && filtered.map((p) => {
                  const d = drivers.find((x) => x.id === p.driver_id);
                  return (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs font-semibold">{p.tracking_id}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{p.receiver_name ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">{p.dropoff_addr ?? "—"}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">{d?.name ?? "—"}</td>
                      <td className="whitespace-nowrap px-4 py-3"><StatusPill status={toLegacyStatus(p.status)} /></td>
                      <td className="whitespace-nowrap px-4 py-3 font-medium">{formatParcelValue(p.amount_kobo)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{formatDateTime(p.updated_at)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setViewParcel(p)}>
                          <Eye className="mr-1 h-3.5 w-3.5" /> View
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>
                          <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                        </Button>
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/app/tracking/${p.id}`}>Track</Link>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && filtered.length === 0 ? (
                  <tr><td colSpan={7} className="p-10 text-center text-sm text-muted-foreground">No deliveries match your filters.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={newOpen} onOpenChange={(v) => (v ? setNewOpen(true) : requestClose())}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New delivery</DialogTitle>
            <DialogDescription>
              Parcels are carried by whichever vehicle the assigned driver currently drives — pick a driver, not a vehicle.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="sender_contact">Sender</Label>
              <SearchableSelect
                id="sender_contact"
                value={form.sender_contact || "manual"}
                placeholder="Type manually"
                searchPlaceholder="Search contacts…"
                onValueChange={(v) => {
                  if (v === "manual") {
                    setForm({ ...form, sender_contact: "" });
                  } else if (v === "__new__") {
                    setForm({ ...form, sender_contact: "__new__", sender_name: "", sender_phone: "", sender_email: "" });
                  } else {
                    const c = clients.find((x) => x.id === v);
                    setForm({ ...form, sender_contact: v, sender_name: c?.name ?? "", sender_phone: c?.phone ?? "", sender_email: c?.email ?? "" });
                  }
                }}
                options={[
                  { value: "manual", label: "Type manually" },
                  { value: "__new__", label: "+ Create new contact" },
                  ...clients.map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            </div>
            <div />
            {form.sender_contact === "__new__" ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="new_sender_name">New sender name *</Label>
                  <Input id="new_sender_name" value={form.new_sender_name} onChange={(e) => setForm({ ...form, new_sender_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new_sender_phone">New sender phone</Label>
                  <Input id="new_sender_phone" value={form.new_sender_phone} onChange={(e) => setForm({ ...form, new_sender_phone: e.target.value })} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="new_sender_email">New sender email</Label>
                  <Input id="new_sender_email" type="email" value={form.new_sender_email} onChange={(e) => setForm({ ...form, new_sender_email: e.target.value })} />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="sender_name">Sender name *</Label>
                  <Input id="sender_name" value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sender_phone">Sender phone</Label>
                  <Input id="sender_phone" value={form.sender_phone} onChange={(e) => setForm({ ...form, sender_phone: e.target.value })} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="sender_email">Sender email</Label>
                  <Input id="sender_email" type="email" value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} />
                </div>
              </>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="receiver_contact">Receiver</Label>
              <SearchableSelect
                id="receiver_contact"
                value={form.receiver_contact || "manual"}
                placeholder="Type manually"
                searchPlaceholder="Search contacts…"
                onValueChange={(v) => {
                  if (v === "manual") {
                    setForm({ ...form, receiver_contact: "" });
                  } else if (v === "__new__") {
                    setForm({ ...form, receiver_contact: "__new__", receiver_name: "", receiver_phone: "", receiver_email: "" });
                  } else {
                    const c = clients.find((x) => x.id === v);
                    setForm({ ...form, receiver_contact: v, receiver_name: c?.name ?? "", receiver_phone: c?.phone ?? "", receiver_email: c?.email ?? "" });
                  }
                }}
                options={[
                  { value: "manual", label: "Type manually" },
                  { value: "__new__", label: "+ Create new contact" },
                  ...clients.map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            </div>
            <div />
            {form.receiver_contact === "__new__" ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="new_receiver_name">New receiver name *</Label>
                  <Input id="new_receiver_name" value={form.new_receiver_name} onChange={(e) => setForm({ ...form, new_receiver_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new_receiver_phone">New receiver phone</Label>
                  <Input id="new_receiver_phone" value={form.new_receiver_phone} onChange={(e) => setForm({ ...form, new_receiver_phone: e.target.value })} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="new_receiver_email">New receiver email</Label>
                  <Input id="new_receiver_email" type="email" value={form.new_receiver_email} onChange={(e) => setForm({ ...form, new_receiver_email: e.target.value })} />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="receiver_name">Receiver name *</Label>
                  <Input id="receiver_name" value={form.receiver_name} onChange={(e) => setForm({ ...form, receiver_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="receiver_phone">Receiver phone</Label>
                  <Input id="receiver_phone" value={form.receiver_phone} onChange={(e) => setForm({ ...form, receiver_phone: e.target.value })} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="receiver_email">Receiver email</Label>
                  <Input id="receiver_email" type="email" value={form.receiver_email} onChange={(e) => setForm({ ...form, receiver_email: e.target.value })} />
                </div>
              </>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="pickup_addr">Pickup address *</Label>
              <Input
                id="pickup_addr"
                value={form.pickup_addr}
                onChange={(e) => setForm({ ...form, pickup_addr: e.target.value })}
                onBlur={() => resolveZoneFromAddress(form.pickup_addr)}
              />
              {postcodeEnabled ? (
                <PostcodeAutocomplete
                  token={token}
                  label="Have a postcode? Type it"
                  onResolved={(address, postcode) => {
                    setForm((f) => ({ ...f, pickup_addr: address }));
                    const zone = postcode.split("-")[0]?.toUpperCase();
                    if (zone) {
                      postcodeApi.zoneSuggestion(zone, token).then(setNewDeliveryZoneSuggestion).catch(() => {});
                    }
                  }}
                />
              ) : null}
              {newDeliveryZoneSuggestion ? (
                <div className="rounded-md border border-dashed bg-muted/40 px-2.5 py-1.5 text-[11px] text-muted-foreground">
                  Zone {newDeliveryZoneSuggestion.zone}: suggested fee ×{newDeliveryZoneSuggestion.fee_multiplier}
                  {newDeliveryZoneSuggestion.suggested_driver ? (
                    <> · suggested driver <span className="font-medium">{newDeliveryZoneSuggestion.suggested_driver.name}</span></>
                  ) : null}
                  {" "}— informational only
                </div>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dropoff_addr">Dropoff address *</Label>
              <Input id="dropoff_addr" value={form.dropoff_addr} onChange={(e) => setForm({ ...form, dropoff_addr: e.target.value })} />
              {postcodeEnabled ? (
                <PostcodeAutocomplete
                  token={token}
                  label="Have a postcode? Type it"
                  onResolved={(address) => setForm((f) => ({ ...f, dropoff_addr: address }))}
                />
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="amount_naira">Declared value (₦) *</Label>
              <Input
                id="amount_naira"
                type="number"
                min="0"
                value={form.amount_naira}
                onChange={(e) => setForm({ ...form, amount_naira: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="driver_id">Assign driver</Label>
              <SearchableSelect
                id="driver_id"
                value={form.driver_id || "none"}
                placeholder="Unassigned"
                searchPlaceholder="Search drivers…"
                onValueChange={(v) => setForm({ ...form, driver_id: v === "none" ? "" : v })}
                options={[
                  { value: "none", label: "Unassigned — decide later" },
                  ...drivers.map((d) => ({
                    value: d.id,
                    label: `${d.name}${d.vehicle_plate ? ` · ${d.vehicle_plate}` : " · no vehicle"}`,
                  })),
                ]}
              />
            </div>
            {!hideCompanySelector ? (
              <div className="space-y-1.5">
                <Label htmlFor="corporate_account_id">Company *</Label>
                <SearchableSelect
                  id="corporate_account_id"
                  value={form.corporate_account_id || "none"}
                  placeholder="Select company"
                  searchPlaceholder="Search companies…"
                  onValueChange={(v) => setForm({ ...form, corporate_account_id: v === "none" ? "" : v })}
                  options={[
                    { value: "none", label: "Select company" },
                    ...corporates.map((c) => ({ value: c.id, label: c.company })),
                  ]}
                />
              </div>
            ) : null}
            <div className="space-y-1.5">
              <Label htmlFor="client_id">Billed client</Label>
              <SearchableSelect
                id="client_id"
                value={form.client_id || "none"}
                placeholder="No client account"
                searchPlaceholder="Search clients…"
                onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}
                options={[
                  { value: "none", label: "No client account" },
                  { value: "__new__", label: "+ Create new client" },
                  ...clients.map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            </div>
            {form.client_id === "__new__" ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="new_client_name">New client name *</Label>
                  <Input id="new_client_name" value={form.new_client_name} onChange={(e) => setForm({ ...form, new_client_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new_client_phone">New client phone</Label>
                  <Input id="new_client_phone" value={form.new_client_phone} onChange={(e) => setForm({ ...form, new_client_phone: e.target.value })} />
                </div>
              </>
            ) : null}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="item_description">Item description</Label>
              <Textarea
                id="item_description"
                rows={3}
                placeholder="What's being shipped…"
                value={form.item_description}
                onChange={(e) => setForm({ ...form, item_description: e.target.value })}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Intake photo</Label>
              <ImagePicker
                previewUrl={intakeImagePreview}
                onPick={pickIntakeImage}
                onClear={clearIntakeImage}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={requestClose} disabled={creating}>Cancel</Button>
            <Button onClick={createDelivery} disabled={creating}>{creating ? "Creating…" : "Create delivery"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this delivery?</AlertDialogTitle>
            <AlertDialogDescription>
              You've entered details that haven't been saved. Closing now will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={discardAndClose}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editParcel} onOpenChange={(v) => (v ? null : requestEditClose())}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit delivery {editParcel?.tracking_id}</DialogTitle>
            <DialogDescription>
              Reassigning the driver moves whichever vehicle they currently drive onto this delivery.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="e_receiver_name">Receiver name *</Label>
              <Input id="e_receiver_name" value={editForm.receiver_name} onChange={(e) => setEditForm({ ...editForm, receiver_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="e_receiver_phone">Receiver phone</Label>
              <Input id="e_receiver_phone" value={editForm.receiver_phone} onChange={(e) => setEditForm({ ...editForm, receiver_phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="e_receiver_email">Receiver email</Label>
              <Input id="e_receiver_email" type="email" value={editForm.receiver_email} onChange={(e) => setEditForm({ ...editForm, receiver_email: e.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="e_pickup_addr">Pickup address *</Label>
              <Input id="e_pickup_addr" value={editForm.pickup_addr} onChange={(e) => setEditForm({ ...editForm, pickup_addr: e.target.value })} />
              {editParcel?.pickup_postcode ? (
                <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  NIPOST-verified · {editParcel.pickup_postcode}
                </span>
              ) : null}
              {postcodeEnabled ? (
                <PostcodeAutocomplete
                  token={token}
                  label="Have a postcode? Type it"
                  onResolved={(address) => setEditForm((f) => ({ ...f, pickup_addr: address }))}
                />
              ) : null}
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="e_dropoff_addr">Dropoff address *</Label>
              <Input id="e_dropoff_addr" value={editForm.dropoff_addr} onChange={(e) => setEditForm({ ...editForm, dropoff_addr: e.target.value })} />
              {editParcel?.dropoff_postcode ? (
                <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  NIPOST-verified · {editParcel.dropoff_postcode}
                </span>
              ) : null}
              {postcodeEnabled ? (
                <PostcodeAutocomplete
                  token={token}
                  label="Have a postcode? Type it"
                  onResolved={(address) => setEditForm((f) => ({ ...f, dropoff_addr: address }))}
                />
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="e_amount_naira">Declared value (₦) *</Label>
              <Input
                id="e_amount_naira"
                type="number"
                min="0"
                value={editForm.amount_naira}
                onChange={(e) => setEditForm({ ...editForm, amount_naira: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="e_driver_id">Assign driver</Label>
              <SearchableSelect
                id="e_driver_id"
                value={editForm.driver_id || "none"}
                placeholder="Unassigned"
                searchPlaceholder="Search drivers…"
                onValueChange={(v) => setEditForm({ ...editForm, driver_id: v === "none" ? "" : v })}
                options={[
                  { value: "none", label: "Unassigned — decide later" },
                  ...drivers.map((d) => ({
                    value: d.id,
                    label: `${d.name}${d.vehicle_plate ? ` · ${d.vehicle_plate}` : " · no vehicle"}`,
                  })),
                ]}
              />
            </div>
            {!hideCompanySelector ? (
              <div className="space-y-1.5">
                <Label htmlFor="e_corporate_account_id">Company</Label>
                <SearchableSelect
                  id="e_corporate_account_id"
                  value={editForm.corporate_account_id || "none"}
                  placeholder="Select company"
                  searchPlaceholder="Search companies…"
                  onValueChange={(v) => setEditForm({ ...editForm, corporate_account_id: v === "none" ? "" : v })}
                  options={[
                    { value: "none", label: "Select company" },
                    ...corporates.map((c) => ({ value: c.id, label: c.company })),
                  ]}
                />
              </div>
            ) : null}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Delivery photo</Label>
              <ImagePicker
                previewUrl={null}
                busy={editUploadingImage}
                onPick={async (file) => {
                  if (!file || !editParcel) return;
                  setEditUploadingImage(true);
                  try {
                    await parcelsApi.uploadImage(editParcel.id, file, "delivery_proof", token);
                    toast.success("Photo uploaded");
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : "Photo upload failed");
                  } finally {
                    setEditUploadingImage(false);
                  }
                }}
                onClear={() => {}}
              />
            </div>
          </div>

          {editParcel && editParcel.status !== "delivered" && editParcel.status !== "failed" ? (
            <div className="space-y-3 rounded-lg border p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Proof of delivery
              </div>
              <p className="text-xs text-muted-foreground">
                Requires a delivery photo/signature on file, or a verified delivery code, before this can be marked delivered.
              </p>

              <div className="space-y-1.5">
                <Label>Signature</Label>
                {signatureSaved ? (
                  <div className="text-xs text-success">Signature saved.</div>
                ) : (
                  <SignaturePad
                    onCapture={async (file) => {
                      if (!editParcel) return;
                      setUploadingSignature(true);
                      try {
                        await parcelsApi.uploadImage(editParcel.id, file, "signature", token);
                        setSignatureSaved(true);
                        toast.success("Signature saved");
                      } catch (err) {
                        toast.error(err instanceof Error ? err.message : "Signature upload failed");
                      } finally {
                        setUploadingSignature(false);
                      }
                    }}
                  />
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Delivery code (OTP)</Label>
                {otpVerified ? (
                  <div className="text-xs text-success">Code verified.</div>
                ) : otpChannel ? (
                  <div className="flex gap-2">
                    <Input
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="6-digit code"
                      className="max-w-[160px]"
                    />
                    <Button type="button" size="sm" disabled={otpVerifying || otpCode.length !== 6} onClick={verifyOtp}>
                      {otpVerifying ? "Verifying…" : "Verify"}
                    </Button>
                    <Button type="button" size="sm" variant="outline" disabled={otpRequesting} onClick={requestOtp}>
                      Resend
                    </Button>
                  </div>
                ) : (
                  <Button type="button" size="sm" variant="outline" disabled={otpRequesting} onClick={requestOtp}>
                    {otpRequesting ? "Sending…" : "Send code to receiver"}
                  </Button>
                )}
              </div>

              <Button
                type="button"
                className="w-full"
                disabled={markingDelivered || uploadingSignature}
                onClick={markDelivered}
              >
                {markingDelivered ? "Marking delivered…" : "Mark as delivered"}
              </Button>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={requestEditClose} disabled={saving}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmEditCloseOpen} onOpenChange={setConfirmEditCloseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You've made changes that haven't been saved. Closing now will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={discardEditAndClose}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!viewParcel} onOpenChange={(v) => (v ? null : setViewParcel(null))}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Delivery {viewParcel?.tracking_id}</DialogTitle>
            <DialogDescription>Full details for this delivery.</DialogDescription>
          </DialogHeader>
          {viewParcel ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <StatusPill status={toLegacyStatus(viewParcel.status)} />
                <span className="text-sm font-medium">{formatParcelValue(viewParcel.amount_kobo)}</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <div className="text-xs text-muted-foreground">Receiver</div>
                  <div className="text-sm font-medium">{viewParcel.receiver_name ?? "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Driver</div>
                  <div className="text-sm font-medium">{drivers.find((d) => d.id === viewParcel.driver_id)?.name ?? "Unassigned"}</div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-xs text-muted-foreground">Pickup address</div>
                  <div className="text-sm">{viewParcel.pickup_addr ?? "—"}</div>
                  {viewParcel.pickup_postcode ? (
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                      NIPOST-verified · {viewParcel.pickup_postcode}
                    </span>
                  ) : null}
                </div>
                <div className="sm:col-span-2">
                  <div className="text-xs text-muted-foreground">Dropoff address</div>
                  <div className="text-sm">{viewParcel.dropoff_addr ?? "—"}</div>
                  {viewParcel.dropoff_postcode ? (
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                      NIPOST-verified · {viewParcel.dropoff_postcode}
                    </span>
                  ) : null}
                </div>
                {!hideCompanySelector ? (
                  <div>
                    <div className="text-xs text-muted-foreground">Company</div>
                    <div className="text-sm">{corporates.find((c) => c.id === viewParcel.corporate_account_id)?.company ?? "—"}</div>
                  </div>
                ) : null}
                <div>
                  <div className="text-xs text-muted-foreground">Client</div>
                  <div className="text-sm">{clients.find((c) => c.id === viewParcel.client_id)?.name ?? "—"}</div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-xs text-muted-foreground">Item description</div>
                  <div className="text-sm">{viewParcel.item_description ?? "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Created</div>
                  <div className="text-sm">{formatDateTime(viewParcel.created_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Last updated</div>
                  <div className="text-sm">{formatDateTime(viewParcel.updated_at)}</div>
                </div>
              </div>

              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Photos</div>
                {viewImagesLoading ? (
                  <div className="text-sm text-muted-foreground">Loading photos…</div>
                ) : viewImages.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No photos uploaded for this delivery.</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {viewImages.map((img) =>
                      img.url ? (
                        <a key={img.id} href={img.url} target="_blank" rel="noreferrer">
                          <img
                            src={img.url}
                            alt={img.kind}
                            className="h-24 w-24 rounded-lg border object-cover"
                          />
                        </a>
                      ) : null,
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewParcel(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) setBulkRows([]); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk postcode validation</DialogTitle>
            <DialogDescription>
              Upload a CSV or plain-text file with one address per line (or an "address" column). Each row is geocoded
              and checked against NIPOST — no deliveries are created, this just previews what postcodes would resolve.
            </DialogDescription>
          </DialogHeader>
          <input
            ref={bulkFileRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void runBulkValidate(f);
              e.target.value = "";
            }}
          />
          <Button variant="outline" size="sm" onClick={() => bulkFileRef.current?.click()} disabled={bulkRunning}>
            <Upload className="mr-1.5 h-4 w-4" /> {bulkRunning ? "Validating…" : "Choose file"}
          </Button>
          {bulkRows.length > 0 ? (
            <div className="max-h-80 overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    <th className="p-2 text-left font-medium">Address</th>
                    <th className="p-2 text-left font-medium">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkRows.map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2 align-top">{r.address}</td>
                      <td className="p-2 align-top">
                        {r.status === "ok" ? (
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                            {r.postcode}
                          </span>
                        ) : r.status === "no_postcode" ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                            Geocoded, no postcode match
                          </span>
                        ) : r.status === "skipped" ? (
                          <span className="text-xs text-muted-foreground">Skipped — {r.error}</span>
                        ) : (
                          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                            {r.error ?? "Failed"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={zoneOpen} onOpenChange={setZoneOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1.5">
              Unassigned deliveries by postcode zone
              <ZoneMappingDialog />
            </DialogTitle>
            <DialogDescription>
              Pending-pickup deliveries grouped by NIPOST zone. Pick a driver per zone and assign the whole group at once.
            </DialogDescription>
          </DialogHeader>
          {zoneLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : zoneGroups.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No unassigned deliveries with a resolved postcode right now.
            </div>
          ) : (
            <div className="max-h-96 space-y-4 overflow-y-auto">
              {zoneGroups.map((g) => (
                <div key={g.zone} className="rounded-lg border p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="font-medium">
                      Zone {g.zone} <span className="text-xs text-muted-foreground">({g.parcels.length} deliveries)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={zoneDriverByZone[g.zone] ?? ""}
                        onValueChange={(v) => setZoneDriverByZone((prev) => ({ ...prev, [g.zone]: v }))}
                      >
                        <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="Pick driver" /></SelectTrigger>
                        <SelectContent>
                          {drivers.map((d) => (
                            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        disabled={zoneAssigning === g.zone}
                        onClick={() => assignZone(g.zone, g.parcels.map((p) => p.id))}
                      >
                        {zoneAssigning === g.zone ? "Assigning…" : "Assign zone"}
                      </Button>
                    </div>
                  </div>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {g.parcels.map((p) => (
                      <li key={p.id}>
                        <span className="font-mono">{p.tracking_id}</span> — {p.pickup_addr} → {p.dropoff_addr}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setZoneOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
